import React, { useCallback, useEffect, useRef } from 'react';
import { Box, Center, Flex, Text, VStack } from '@chakra-ui/react';

import { RelevoInterface } from '../../../../../../../api/types';
import { PageSectionHeading } from '../../../../../../../common/components/PageSectionHeading/PageSectionHeading';
import QrCodeScanner from '../../../../../../../common/components/QrCodeScanner/QrCodeScanner';
import { RadioCard } from '../../../../../../../common/components/RadioCard';
import { ScanModal } from '../../../../../../../common/components/ScanModal/ScanModal';
import HandScanner from '../../../../../../../common/components/Scanner/HandScanner';
import { useScanModalFlow } from '../../../../../../../common/components/Scanner/hooks/use-scan-modal-flow.hook';
import { useTranslations } from '../../../../../../../contexts/LocalizationContext';
import { useUpdateItemsInInventoryCount } from '../../../../../../../services/inventory-counts';
import { useLocalStorage } from '../../../../../../../utils/LocalStorage.hook';
import { extractUId } from '../../../../../../../utils/qrCodesHelper';
import {
  ItemAssignmentScannersModes,
  ItemAssignmentSelectMode,
} from '../../../../../../RelevoAssignment/components/UserSelect/types';
import { useInventoryCountsContext } from '../../../../../context/hooks/use-inventory-counts-context.hook';
import { InventoryCountsUpdateItemsResponse } from '../../../../../model/inventory-counts-update-items-response';

export const Scanning = () => {
  const translations = useTranslations();
  const [selectedScanMode, setSelectedScanMode] = useLocalStorage<ItemAssignmentSelectMode>(
    'selectItemMode',
    ItemAssignmentSelectMode.CAMERA_SCAN,
  );
  const effectiveScanMode =
    selectedScanMode === ItemAssignmentSelectMode.SELECT_CATEGORY
      ? ItemAssignmentSelectMode.CAMERA_SCAN
      : selectedScanMode;

  useEffect(() => {
    if (selectedScanMode === ItemAssignmentSelectMode.SELECT_CATEGORY) {
      setSelectedScanMode(ItemAssignmentSelectMode.CAMERA_SCAN);
    }
  }, [selectedScanMode, setSelectedScanMode]);

  const { currentInventoryCountId } = useInventoryCountsContext();
  const { mutate: updateInventoryCount } = useUpdateItemsInInventoryCount();

  const scannedUidsRef = useRef<Set<string>>(new Set<string>());
  const itemsByUidRef = useRef<Map<string, RelevoInterface>>(new Map<string, RelevoInterface>());
  const {
    isOpen,
    onClose: handleModalClose,
    modalKey: scanModalKey,
    scanStatus,
    scannedItem,
    setScannedItem,
    openError,
    openSuccess,
    handleDuplicate,
  } = useScanModalFlow<RelevoInterface>();

  const handleDuplicateRef = useRef(handleDuplicate);
  const openSuccessRef = useRef(openSuccess);
  const openErrorRef = useRef(openError);

  useEffect(() => {
    handleDuplicateRef.current = handleDuplicate;
    openSuccessRef.current = openSuccess;
    openErrorRef.current = openError;
  }, [handleDuplicate, openSuccess, openError]);

  const handleScan = useCallback(
    (qrCode: string) => {
      const uId = extractUId(qrCode);
      if (!uId) {
        openErrorRef.current();
        return;
      }
      if (scannedUidsRef.current.has(uId)) {
        const item = itemsByUidRef.current.get(uId) ?? null;
        setScannedItem(item);
        handleDuplicateRef.current(uId, item);
        return;
      }

      if (currentInventoryCountId) {
        updateInventoryCount(
          { inventoryCountId: currentInventoryCountId, updateRequest: { uId } },
          {
            onSuccess: (response: InventoryCountsUpdateItemsResponse) => {
              const item = response.scannedItem ?? null;
              if (item) {
                itemsByUidRef.current.set(uId, item);
              }
              if (response.itemAlreadyScanned) {
                setScannedItem(item);
                handleDuplicateRef.current(uId, item);
              } else {
                openSuccessRef.current(item, uId);
              }
              scannedUidsRef.current.add(uId);
            },
            onError: () => {
              openErrorRef.current();
            },
          },
        );
      }
    },
    [currentInventoryCountId, updateInventoryCount, setScannedItem],
  );

  return (
    <VStack alignItems={'start'} p={2}>
      <PageSectionHeading>{translations('inventory_counts_scanning_heading')}</PageSectionHeading>
      <Box pb={1} textAlign={'center'} w={'100%'}>
        <RadioCard
          options={ItemAssignmentScannersModes}
          name="InventoryCountsScanMode"
          defaultValue={effectiveScanMode}
          onChange={setSelectedScanMode}
          width={'100%'}
        />
      </Box>
      <Box w={'100%'}>
        {effectiveScanMode === ItemAssignmentSelectMode.CAMERA_SCAN ? (
          <Flex direction={'column'}>
            <Text fontSize={14} align={'left'} pb={1}>
              {translations('inventory_counts_scanning_description')}
            </Text>
            <Center w={'100%'}>
              <QrCodeScanner onScan={handleScan} maxWidth={'100%'} />
            </Center>
          </Flex>
        ) : (
          <HandScanner onScan={handleScan} showTitle={false} fontSize={'14px'} connectedBg={'beige.50'} />
        )}
      </Box>

      <ScanModal
        key={scanModalKey}
        isOpen={isOpen}
        onClose={handleModalClose}
        status={scanStatus}
        item={scannedItem ?? undefined}
      />
    </VStack>
  );
};
